import { useState, useMemo } from 'react';
import moment from 'moment';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const classNames = (...classes) => classes.filter(Boolean).join(' ');

// Default color mapping for status
const DEFAULT_STATUS_COLORS = {
	completed: 'bg-green-100 text-green-800',
	in_progress: 'bg-indigo-100 text-indigo-800',
	scheduled: 'bg-blue-100 text-blue-800',
	pending: 'bg-yellow-100 text-yellow-800',
	on_hold: 'bg-orange-100 text-orange-800',
	cancelled: 'bg-red-100 text-red-800',
	draft: 'bg-gray-100 text-gray-800',
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Single calendar day cell
 */
const CalendarDay = ({
	date,
	isCurrentMonth,
	isToday,
	events,
	maxEventsToShow = 3,
	onEventClick,
	onDayClick,
	getEventColor,
	getEventLabel,
}) => {
	const dayStr = date.format('YYYY-MM-DD');
	const visibleEvents = events.slice(0, maxEventsToShow);
	const hiddenCount = events.length - maxEventsToShow;

	console.log('visibleEvents: ', visibleEvents);
	return (
		<div
			className={classNames(
				'min-h-[100px] p-1 border border-gray-100 rounded transition-colors',
				isCurrentMonth ? 'bg-white' : 'bg-gray-50',
				onDayClick && 'cursor-pointer hover:bg-gray-50',
			)}
			onClick={() => onDayClick?.(date, events)}
		>
			{/* Day number */}
			<div
				className={classNames(
					'text-xs font-medium mb-1',
					isToday
						? 'bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center'
						: isCurrentMonth
							? 'text-gray-900'
							: 'text-gray-400',
				)}
			>
				{date.format('D')}
			</div>

			{/* Events */}
			<div className="space-y-1">
				{visibleEvents.map((event) => (
					<div
						key={event.id}
						onClick={(e) => {
							e.stopPropagation();
							onEventClick?.(event);
						}}
						className={classNames(
							'text-xs p-1 rounded truncate',
							onEventClick && 'cursor-pointer hover:opacity-80',
							getEventColor?.(event) ||
								DEFAULT_STATUS_COLORS[event.status] ||
								'bg-gray-100 text-gray-800',
						)}
						title={getEventLabel?.(event) || event.title || 'No title'}
					>
						{getEventLabel?.(event) || event.title || 'No title'}
					</div>
				))}
				{hiddenCount > 0 && (
					<div className="text-xs text-gray-500 pl-1">+{hiddenCount} more</div>
				)}
			</div>
		</div>
	);
};

/**
 * Calendar header with navigation
 */
const CalendarHeader = ({
	currentMonth,
	onPrevMonth,
	onNextMonth,
	onToday,
	showTodayButton = true,
}) => (
	<div className="flex items-center justify-between p-4 border-b border-gray-200">
		<div className="flex items-center gap-2">
			<button
				onClick={onPrevMonth}
				className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
				aria-label="Previous month"
			>
				<ChevronLeftIcon className="w-5 h-5 text-gray-600" />
			</button>
			{showTodayButton && (
				<button
					onClick={onToday}
					className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
				>
					Today
				</button>
			)}
			<button
				onClick={onNextMonth}
				className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
				aria-label="Next month"
			>
				<ChevronRightIcon className="w-5 h-5 text-gray-600" />
			</button>
		</div>
		<h2 className="text-lg font-semibold text-gray-900">
			{currentMonth.format('MMMM YYYY')}
		</h2>
		<div className="w-[120px]" /> {/* Spacer for centering */}
	</div>
);

/**
 * Day of week headers
 */
const WeekHeader = ({ startOnMonday = true }) => {
	const days = startOnMonday
		? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
		: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

	return (
		<div className="grid grid-cols-7 gap-1 mb-2">
			{days.map((day) => (
				<div
					key={day}
					className="text-center text-xs font-medium text-gray-500 py-2"
				>
					{day}
				</div>
			))}
		</div>
	);
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Calendar component for displaying events/items on a monthly calendar view.
 *
 * @param {Object} props
 * @param {Array} props.events - Array of event objects with start/end dates
 * @param {moment.Moment} props.currentMonth - The current month to display (controlled)
 * @param {Function} props.onMonthChange - Callback when month changes (for controlled mode)
 * @param {Function} props.onEventClick - Callback when an event is clicked
 * @param {Function} props.onDayClick - Callback when a day cell is clicked
 * @param {Function} props.getEventColor - Custom function to get event color classes
 * @param {Function} props.getEventLabel - Custom function to get event display label
 * @param {Function} props.getEventDateRange - Custom function to get event start/end dates
 * @param {number} props.maxEventsPerDay - Max events to show per day before "+X more"
 * @param {boolean} props.loading - Show loading overlay
 * @param {boolean} props.startOnMonday - Start week on Monday (default true)
 * @param {boolean} props.showTodayButton - Show "Today" button in header
 * @param {string} props.containerClassName - Additional classes for container
 */
const Calendar = ({
	events = [],
	currentMonth: controlledMonth,
	onMonthChange,
	onEventClick,
	onDayClick,
	getEventColor,
	getEventLabel,
	getEventDateRange,
	maxEventsPerDay = 3,
	loading = false,
	startOnMonday = true,
	showTodayButton = true,
	containerClassName = '',
}) => {
	// Internal state for uncontrolled mode
	const [internalMonth, setInternalMonth] = useState(moment().startOf('month'));

	// Use controlled or uncontrolled month
	const currentMonth = controlledMonth || internalMonth;
	const setCurrentMonth = onMonthChange || setInternalMonth;

	// Generate calendar days
	const calendarDays = useMemo(() => {
		const startOfMonth = currentMonth.clone().startOf('month');
		const endOfMonth = currentMonth.clone().endOf('month');
		const startOfWeek = startOfMonth
			.clone()
			.startOf(startOnMonday ? 'isoWeek' : 'week');
		const endOfWeek = endOfMonth
			.clone()
			.endOf(startOnMonday ? 'isoWeek' : 'week');

		const days = [];
		let day = startOfWeek.clone();

		while (day.isSameOrBefore(endOfWeek, 'day')) {
			days.push(day.clone());
			day.add(1, 'day');
		}

		return days;
	}, [currentMonth, startOnMonday]);

	// Group events by day
	const eventsByDay = useMemo(() => {
		const grouped = {};

		events.forEach((event) => {
			// Get date range using custom function or default fields
			let start, end;
			if (getEventDateRange) {
				const range = getEventDateRange(event);
				start = range.start;
				end = range.end;
			} else {
				start = event.start || event.scheduledStartDate;
				end = event.end || event.scheduledEndDate || start;
			}

			if (!start) return;

			// Use UTC for date-only fields (like task due dates) to avoid timezone shift
			// Tasks have type: 'task' and only have dueDate (no time component)
			const isDateOnly = event.type === 'task' || !event.end;
			const startMoment = isDateOnly ? moment.utc(start) : moment(start);
			const endMoment = isDateOnly ? moment.utc(end || start) : moment(end || start);

			// Add event to each day it spans
			let current = startMoment.clone().startOf('day');
			const endDay = endMoment.clone().startOf('day');

			while (current.isSameOrBefore(endDay, 'day')) {
				const dayKey = current.format('YYYY-MM-DD');
				if (!grouped[dayKey]) {
					grouped[dayKey] = [];
				}
				grouped[dayKey].push(event);
				current.add(1, 'day');
			}
		});

		return grouped;
	}, [events, getEventDateRange]);

	// Navigation handlers
	const handlePrevMonth = () => {
		setCurrentMonth(currentMonth.clone().subtract(1, 'month'));
	};

	const handleNextMonth = () => {
		setCurrentMonth(currentMonth.clone().add(1, 'month'));
	};

	const handleToday = () => {
		setCurrentMonth(moment().startOf('month'));
	};

	return (
		<div
			className={classNames(
				'bg-white border border-gray-200 rounded-lg relative',
				containerClassName,
			)}
		>
			<CalendarHeader
				currentMonth={currentMonth}
				onPrevMonth={handlePrevMonth}
				onNextMonth={handleNextMonth}
				onToday={handleToday}
				showTodayButton={showTodayButton}
			/>

			<div className="p-4">
				<WeekHeader startOnMonday={startOnMonday} />

				<div className="grid grid-cols-7 gap-1">
					{calendarDays.map((dayMoment) => {
						const dayStr = dayMoment.format('YYYY-MM-DD');
						const dayEvents = eventsByDay[dayStr] || [];

						return (
							<CalendarDay
								key={dayStr}
								date={dayMoment}
								isCurrentMonth={dayMoment.isSame(currentMonth, 'month')}
								isToday={dayMoment.isSame(moment(), 'day')}
								events={dayEvents}
								maxEventsToShow={maxEventsPerDay}
								onEventClick={onEventClick}
								onDayClick={onDayClick}
								getEventColor={getEventColor}
								getEventLabel={getEventLabel}
							/>
						);
					})}
				</div>
			</div>

			{/* Loading overlay */}
			{loading && (
				<div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-lg">
					<div className="text-gray-500">Loading...</div>
				</div>
			)}
		</div>
	);
};

export default Calendar;
export { CalendarDay, CalendarHeader, WeekHeader, DEFAULT_STATUS_COLORS };
